import assert from "assert";

const URL_DJANGO_BASE = process.env.REACT_APP_URL_DJANGO;
assert(URL_DJANGO_BASE !== undefined, "Env variable missing");

const URL_DJANGO_v0 = URL_DJANGO_BASE + "/api/v0";
const URL_DJANGO_v1 = URL_DJANGO_BASE + "/api/v1";

const URL_ONBOARD = process.env.REACT_APP_URL_ONBOARD;
assert(URL_ONBOARD !== undefined, "Env variable missing REACT_APP_URL_ONBOARD");

const URL_MARKETING_CONSENT = process.env.REACT_APP_URL_MARKETING_CONSENT;
assert(
  URL_MARKETING_CONSENT !== undefined,
  "Env variable missing REACT_APP_URL_MARKETING_CONSENT",
);

const URL_DOCS = process.env.REACT_APP_URL_DOCS;
assert(URL_DOCS !== undefined, "Env variable missing REACT_APP_URL_DOCS");

interface RegionRawEnvVar {
  KEY: string;
  URL: string;
  URL_GRAPHQL: string;
  URL_DJANGO: string;
}

const isRegionEnv = (value: unknown): value is RegionRawEnvVar => {
  return (
    value != null &&
    typeof (value as Partial<RegionRawEnvVar>).KEY === "string" &&
    typeof (value as Partial<RegionRawEnvVar>).URL === "string" &&
    typeof (value as Partial<RegionRawEnvVar>).URL_GRAPHQL === "string" &&
    typeof (value as Partial<RegionRawEnvVar>).URL_DJANGO === "string"
  );
};

export type TRegionsEnv = RegionRawEnvVar[];
export const isRegionsEnvArray = (value: unknown): value is TRegionsEnv => {
  return Array.isArray(value) && value.every((item) => isRegionEnv(item));
};

const _EXTERNAL_REGIONS = isRegionsEnvArray(
  JSON.parse(process.env.REACT_APP_EXTERNAL_REGIONS ?? ""),
)
  ? (JSON.parse(process.env.REACT_APP_EXTERNAL_REGIONS ?? "") as TRegionsEnv)
  : undefined;

assert(
  _EXTERNAL_REGIONS !== undefined,
  "process.env.REACT_APP_EXTERNAL_REGIONS is undefined",
);

/**
 * Region key->name mapping
 */
const regionNameMap: Record<
  TRegionsEnv[number]["KEY"],
  {
    shortName: string;
    longName: string;
  }
> = {
  // https://inscopix.atlassian.net/wiki/spaces/I/pages/3858202698/Region+Names
  "ideas-development": { shortName: "DEV", longName: "Development" },
  "ideas-staging": { shortName: "ST1", longName: "Staging" },
  "ideas-staging2": { shortName: "ST2", longName: "Staging 2" },
  "ideas-next": { shortName: "NXT", longName: "Next" },
  local: { shortName: "LOC", longName: "Local" },
  us: { shortName: "USA", longName: "United States" },
  jp: { shortName: "JP", longName: "Japan" },
  eu: { shortName: "EU", longName: "EU (Germany)" },
};

_EXTERNAL_REGIONS?.forEach((region) => {
  assert(regionNameMap[region.KEY] !== undefined, "Region key mismatch");
});

/**
 * Construct current region public var
 */
const ENV = process.env.REACT_APP_ENV;
assert(ENV !== undefined, "process.env.REACT_APP_ENV is undefined");

assert(regionNameMap[ENV] !== undefined, "Region key Mismatch");
const CURRENT_REGION = { KEY: ENV, ...regionNameMap[ENV] };

/**
 * Construct external regions public var
 */
const EXTERNAL_REGIONS = _EXTERNAL_REGIONS.map((region) => ({
  KEY: region.KEY,
  URL: region.URL,
  URL_DJANGO: region.URL_DJANGO,
  URL_GRAPHQL: region.URL_GRAPHQL,
  ...regionNameMap[region.KEY],
}));

export type ExternalRegion = (typeof EXTERNAL_REGIONS)[number];

const ENV_MAP = {
  CURRENT_REGION,
  EXTERNAL_REGIONS: EXTERNAL_REGIONS,
  // AWS
  AWS_AUTH_FLOW_TYPE: process.env.REACT_APP_AWS_AUTH_FLOW_TYPE,
  AWS_AUTH_REGION: process.env.REACT_APP_AWS_AUTH_REGION,
  AWS_AUTH_USER_POOL_ID: process.env.REACT_APP_AWS_AUTH_USER_POOL_ID,
  AWS_AUTH_USER_POOL_WEB_CLIENT_ID:
    process.env.REACT_APP_AWS_AUTH_USER_POOL_WEB_CLIENT_ID,
  AWS_AUTH_OAUTH_DOMAIN: process.env.REACT_APP_AWS_AUTH_OAUTH_DOMAIN,
  AWS_AUTH_COOKIE_DOMAIN: process.env.REACT_APP_AWS_AUTH_COOKIE_DOMAIN,
  AWS_AUTH_OAUTH_SCOPE: process.env.REACT_APP_AWS_AUTH_OAUTH_SCOPE,
  AWS_AUTH_OAUTH_REDIRECT_SIGNIN:
    process.env.REACT_APP_AWS_AUTH_OAUTH_REDIRECT_SIGNIN,
  AWS_AUTH_OAUTH_REDIRECT_SIGNOUT:
    process.env.REACT_APP_AWS_AUTH_OAUTH_REDIRECT_SIGNOUT,
  AWS_AUTH_OAUTH_RESPONSE_TYPE:
    process.env.REACT_APP_AWS_AUTH_OAUTH_RESPONSE_TYPE,
  // Graphql
  URL_GRAPHQL: process.env.REACT_APP_URL_GRAPHQL,
  // Django endpoints
  URL_DJANGO_v0,
  URL_DRS_FILE_ARCHIVE: `${URL_DJANGO_v0}/drs/file/archive/`,
  URL_DRS_CODE_FILE: `${URL_DJANGO_v0}/drs/code_file/`,
  URL_DRS_CODE_FILE_UPLOAD_URLS: `${URL_DJANGO_v0}/drs/code_file/upload_urls/`,
  URL_DRS_CODE_FILE_COMPLETE_UPLOAD: `${URL_DJANGO_v0}/drs/code_file/complete_upload/`,
  URL_DRS_FILE_CREATE: `${URL_DJANGO_v0}/drs/file/`,
  URL_DRS_FILE_DOWNLOAD: `${URL_DJANGO_v0}/drs/file/download/`,
  URL_DRS_FILE_DOWNLOAD_PREVIEW: `${URL_DJANGO_v0}/drs/file/download/preview/`,
  URL_DRS_FILE_UPLOAD_URLS: `${URL_DJANGO_v0}/drs/file/upload_urls/`,
  URL_DRS_FILE_COMPLETE_UPLOAD: `${URL_DJANGO_v0}/drs/file/complete_upload/`,
  URL_DRS_FILE_SCHEDULE_DELETE: `${URL_DJANGO_v0}/drs/file/schedule_delete/`,
  URL_DRS_FILE_UNARCHIVE: `${URL_DJANGO_v0}/drs/file/unarchive/`,
  URL_DRS_FILE_SCHEDULE_DATA_DELETE: `${URL_DJANGO_v0}/drs/file/schedule_data_delete/`,
  URL_DRS_FILE_BY_REFERENCE_ID: `${URL_DJANGO_v0}/drs/file/by_reference_id/`,
  URL_METADATA_FILE_METADATA: `${URL_DJANGO_v1}/metadata/filemetadata/`,
  URL_DRS_FILE_SERIES_CREATE: `${URL_DJANGO_v0}/drs/file/series/create/`,
  URL_DRS_FILE_SERIES_REVERT_CREATE: `${URL_DJANGO_v0}/drs/file/series/revert_create/`,
  URL_DRS_FILE_UPDATE: `${URL_DJANGO_v0}/drs/file/`,
  URL_LIBRARY_PROJECT: (baseUrl = URL_DJANGO_v0) =>
    `${baseUrl}/library/project/`,
  URL_LIBRARY_ADMIN_PROJECT: `${URL_DJANGO_v0}/library/admin_project/`,
  URL_LIBRARY_PROJECT_ADMIN_MANAGE: `${URL_DJANGO_v0}/library/project_admin_manage/`,
  URL_LIBRARY_PROJECT_CLONE: `${URL_DJANGO_v0}/library/project/clone/`,
  URL_PROJECT_SCHEDULE_DELETE: `${URL_DJANGO_v0}/library/project/schedule_delete/`,
  URL_LIBRARY_PROJECT_ACCESS: `${URL_DJANGO_v0}/library/project_access/`,
  URL_LIBRARY_PROJECT_ACCESS_ADMIN_MANAGE: `${URL_DJANGO_v0}/library/project_access_admin_manage/`,
  URL_LIBRARY_PROJECT_SHARE: `${URL_DJANGO_v0}/library/project_share/`,
  URL_LIBRARY_PROJECT_SHARE_ADMIN_MANAGE: `${URL_DJANGO_v0}/library/project_share_admin_manage/`,
  URL_LIBRARY_PROJECT_PIN: `${URL_DJANGO_v0}/library/project/pin/`,
  URL_LIBRARY_PROJECT_UNPIN: `${URL_DJANGO_v0}/library/project/unpin/`,
  URL_LIBRARY_PROJECT_FOLDER: `${URL_DJANGO_v0}/library/project_folder/`,
  URL_LIBRARY_DATA_TABLE: `${URL_DJANGO_v0}/library/data_table/`,
  URL_LIBRARY_DATA_TABLE_CELL: `${URL_DJANGO_v0}/library/data_table_cell/`,
  URL_LIBRARY_DATA_TABLE_COLUMN: `${URL_DJANGO_v0}/library/data_table_column/`,
  URL_LIBRARY_DATA_TABLE_COLUMN_GROUP: `${URL_DJANGO_v0}/library/data_table_column_group/`,
  URL_LIBRARY_DATA_TABLE_ROW: `${URL_DJANGO_v0}/library/data_table_row/`,
  URL_LIBRARY_GDT_ANALYSIS_TABLE: `${URL_DJANGO_v0}/library/gdt_analysis_table/`,
  URL_LIBRARY_GDT_ANALYSIS_TABLE_CELL: `${URL_DJANGO_v0}/library/gdt_analysis_table_cell/`,
  URL_LIBRARY_GDT_ANALYSIS_TABLE_COLUMN: `${URL_DJANGO_v0}/library/gdt_analysis_table_column/`,
  URL_LIBRARY_GDT_ANALYSIS_TABLE_COLUMN_GROUP: `${URL_DJANGO_v0}/library/gdt_analysis_table_column_group/`,
  URL_LIBRARY_GDT_ANALYSIS_TABLE_ROW: `${URL_DJANGO_v0}/library/gdt_analysis_table_row/`,
  URL_NOTIFICATION_NOTIFICATION: (regionBaseUrl?: string) =>
    `${
      regionBaseUrl !== undefined ? regionBaseUrl : URL_DJANGO_v0
    }/notification/notification/`,
  URL_NOTIFICATION_BULK_MARK_READ: (regionBaseUrl?: string) =>
    `${
      regionBaseUrl !== undefined ? regionBaseUrl : URL_DJANGO_v0
    }/notification/notification/bulk_mark_read/`,
  URL_NOTIFICATION_NOTIFICATION_UNREAD_COUNT: (regionBaseUrl?: string) =>
    `${
      regionBaseUrl !== undefined ? regionBaseUrl : URL_DJANGO_v0
    }/notification/notification/unread_count/`,
  URL_NOTIFICATION_NOTIFICATION_VALID: (regionBaseUrl?: string) =>
    `${
      regionBaseUrl !== undefined ? regionBaseUrl : URL_DJANGO_v0
    }/notification/notification/valid/`,
  URL_TENANT: `${URL_DJANGO_v0}/tenant/`,
  URL_TENANT_CHECK_QUOTA: `${URL_DJANGO_v0}/tenant/check_quota/`,
  URL_TENANT_OPEN_CONTRACTS: `${URL_DJANGO_v0}/tenant/open_contracts/`,
  URL_TENANT_RESOURCE_SUMMARY: `${URL_DJANGO_v0}/tenant/consumed_resources_summary/`,
  URL_TENANT_MEMBERSHIP: `${URL_DJANGO_v0}/tenant/membership/`,
  URL_TENANT_USER_INVITE: `${URL_DJANGO_v0}/tenant/user-invite/`,
  URL_TENANT_USER_TOUR: `${URL_DJANGO_v0}/tenant/user-tour-status/`,
  URL_TES: `${URL_DJANGO_v0}/tes/`,
  URL_TRS: `${URL_DJANGO_v0}/trs/`,
  URL_TES_CREATE_BULK: `${URL_DJANGO_v0}/tes/task/create_bulk_analysis_table_row/`,
  URL_TES_TASK_CREATE: `${URL_DJANGO_v0}/tes/task/`,
  URL_TES_TASK_CANCEL: `${URL_DJANGO_v0}/tes/task/cancel/`,
  URL_TES_DOWNLOAD_ERR: `${URL_DJANGO_v0}/tes/task/download_error_log/`,
  URL_TES_DOWNLOAD_OUT: `${URL_DJANGO_v0}/tes/task/download_output_log/`,
  URL_TES_CONTAINER_DETAILS: (containerId: number) =>
    `${URL_DJANGO_v0}/tes/image/${containerId}/`,
  URL_TES_CONTAINER_DELETE: (containerId: number) =>
    `${URL_DJANGO_v0}/tes/image/delete/${containerId}/`,
  URL_TRS_TOOL: (toolId: number) => `${URL_DJANGO_v0}/trs/tool/${toolId}/`,
  URL_TRS_TOOL_VERSION: `${URL_DJANGO_v0}/trs/tool/version/`,
  URL_TRS_TOOL_VERSION_TOOL_EDITOR: (toolVersionId: number) =>
    `${URL_DJANGO_v0}/trs/tool/version/${toolVersionId}/tool_editor/`,
  URL_TRS_TOOL_VERSION_DOWNLOAD_TOOL_SPEC: (toolVersionId: number) =>
    `${URL_DJANGO_v0}/trs/tool/version/${toolVersionId}/download_tool_spec/`,
  URL_TRS_TOOL_VERSION_COMPATIBLE: (toolVersionId: number) =>
    `${URL_DJANGO_v0}/trs/tool/version/${toolVersionId}/compatible/`,
  URL_TRS_TOOL_VERSION_DRAFT_COMPATIBLE: (toolVersionId: number) =>
    `${URL_DJANGO_v0}/trs/tool/version/${toolVersionId}/draft_compatible/`,
  URL_TRS_TOOL_VERSION_GENERATE_TABLE: (toolVersionId: number) =>
    `${URL_DJANGO_v0}/trs/tool/version/${toolVersionId}/generate_table/`,
  URL_TRS_TOOL_VERSION_DRAFT_SPEC: (toolVersionId: number) =>
    `${URL_DJANGO_v0}/trs/tool/version/${toolVersionId}/draft_spec/`,
  URL_TRS_TOOL_VERSION_PUBLISH: (toolVersionId: number) =>
    `${URL_DJANGO_v0}/trs/tool/version/${toolVersionId}/publish/`,
  URL_TRS_TOOL_SCHEDULE_DELETE: (toolId: number) =>
    `${URL_DJANGO_v0}/trs/tool/${toolId}/schedule_delete/`,
  URL_TRS_TOOL_VERSION_CHANGELOG_TEMPLATE: (toolVersionId: number) =>
    `${URL_DJANGO_v0}/trs/tool/version/${toolVersionId}/changelog_template/`,
  URL_TRS_TOOL_VERSION_DOCS_TEMPLATE: (toolVersionId: number) =>
    `${URL_DJANGO_v0}/trs/tool/version/${toolVersionId}/docs_template/`,
  URL_TRS_TOOL_VERSION_DRAFT: `${URL_DJANGO_v0}/trs/tool/version/draft/`,
  URL_SYSTEM_HEALTH: `${URL_DJANGO_v0}/system/health/`,
  URL_CONTACT_SUPPORT: `${URL_DJANGO_v0}/system/contact-support/`,
  URL_TENANT_FIND_USER: (regionBaseUrl: string = URL_DJANGO_v0) =>
    `${regionBaseUrl}/tenant/users/find_user/`,
  URL_TENANT_USERS_PERSONAL_SPACE: `${URL_DJANGO_v0}/tenant/users-personal-space/`,
  // other
  URL_DOCS,
  URL_DOCS_MAPPER: `${URL_DOCS}/mapper-v2.json`,
  // Other libraries
  AG_GRID_LICENSE: process.env.REACT_APP_AG_GRID_LICENSE,
  SENTRY_DSN: process.env.REACT_APP_SENTRY_DSN,
  URL_ONBOARD,
  URL_MARKETING_CONSENT,
  GDT: process.env.REACT_APP_GDT ?? "0",
  FF_DEMO_PROJECTS: process.env.REACT_APP_FF_DEMO_PROJECTS ?? "0",
  FF_PERSONAL_SPACE: process.env.REACT_APP_FF_PERSONAL_SPACE ?? "0",
} as const;

export const AWS_CONFIG = {
  Auth: {
    region: ENV_MAP.AWS_AUTH_REGION,
    userPoolId: ENV_MAP.AWS_AUTH_USER_POOL_ID,
    userPoolWebClientId: ENV_MAP.AWS_AUTH_USER_POOL_WEB_CLIENT_ID,
    authenticationFlowType: ENV_MAP.AWS_AUTH_FLOW_TYPE,
    cookieStorage: {
      domain: ENV_MAP.AWS_AUTH_COOKIE_DOMAIN,
      path: "/",
      expires: 30,
      sameSite: "strict",
      secure: ENV_MAP.AWS_AUTH_COOKIE_DOMAIN !== "localhost", //https://github.com/aws-amplify/amplify-js/issues/1736#issuecomment-452398549
    },
    oauth: {
      domain: ENV_MAP.AWS_AUTH_OAUTH_DOMAIN,
      scope: [ENV_MAP.AWS_AUTH_OAUTH_SCOPE],
      redirectSignIn: ENV_MAP.AWS_AUTH_OAUTH_REDIRECT_SIGNIN,
      redirectSignOut: ENV_MAP.AWS_AUTH_OAUTH_REDIRECT_SIGNOUT,
      responseType: ENV_MAP.AWS_AUTH_OAUTH_RESPONSE_TYPE,
    },
  },
} as const;

/**
 * Verify that all environment variables are set
 */
Object.entries(ENV_MAP).forEach(([key, val]) => {
  if (val === undefined) {
    throw new Error(`process.env.REACT_APP_${key} is not defined!`);
  }
});

/**
 * Gets the value of an environment variable
 * @param varName
 * @returns The value of an environment variable
 */
export function getEnvVar<T extends keyof typeof ENV_MAP>(varName: T) {
  const envVar = ENV_MAP[varName];

  if (envVar === undefined) {
    throw new Error(`${varName} is not defined!`);
  }

  return envVar;
}

/**
 * Gets the value of an AWS config variable
 * @param varName
 * @returns The value of an AWS config variable
 */
export function getAwsConfigVar(varName: keyof typeof AWS_CONFIG) {
  const configVar = AWS_CONFIG[varName];

  if (configVar === undefined) {
    throw new Error(`${varName} is not defined!`);
  }

  return configVar;
}
